import {
  QueryFunction,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";

import { useAuth, axios } from "@moonscape/shared";

export const usePublicPricesQueryKey = ["publicPrices"];

export type AssetPriceInfo = {
  gbp: number;
  gbp_24h_change: number;
  symbol: string;
  name: string;
};

export type AssetPrices = {
  [key: string]: AssetPriceInfo;
};
export type PricesResponse = AssetPrices;

export const usePublicPrices = (
  currency: string
): Partial<UseQueryResult<PricesResponse, Error | undefined>> => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  /** fetch function that react-query will use to fetch public prices data */
  const getPublicPrices: QueryFunction<PricesResponse> = async () => {
    try {
      const res = await axios.get(`/public/prices/${currency}`);

      // console.log("usePublicPrices res: ", res);
      console.log("calling /public/prices");
      return res.data;
    } catch (error) {
      console.log("usePublicPrices error", error);
      // For React Query to determine a query has errored, the query function must throw. Any error that is thrown in the query function will be persisted on the error state of the query.
      // https://react-query-v3.tanstack.com/guides/query-functions#handling-and-throwing-errors
      throw error;
    }
  };

  /** used to pre-load prices since they take time */
  const prefetchQueryData = async () => {
    return await queryClient.prefetchQuery(
      [usePublicPricesQueryKey],
      getPublicPrices
    );
  };

  const {
    isLoading: isQueryLoading,
    isSuccess,
    isIdle,
    isError,
    data,
    // metadata
    dataUpdatedAt,
    dataFetchedAt,
  } = useQuery({
    queryKey: [usePublicPricesQueryKey],
    queryFn: getPublicPrices,

    // The query will not execute until the userId exists
    refetchOnWindowFocus: true,

    // Adding staleTime
    staleTime: 3 * 60 * 1000, // 3 minutes

    onError: (error) => {
      console.log("[me] useQuery onError", error);

      // toast.error(`Something went wrong: ${error?.message}`)
    },
  });

  return {
    isLoading: isQueryLoading,
    isIdle,
    isError,
    data,
    dataUpdatedAt,
    dataFetchedAt,
    prefetchQueryData,
  } as Partial<UseQueryResult<PricesResponse, Error | undefined>>;
};
